/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { ISalesOrder } from './components/get-sales-order';
import { IExtendedOrderInformation, IHeader, IList, IOrderHistoryViewProps } from './lli-order-history';

/* Comments next to FC parameters are to show what displays, not what's in the variable. 
   Variables in "list" are processed in ./components/get-sales-order.tsx  */

/***** Hierarchy of elements ****************************************

+ <OrderHistoryView>
    + header <OrderHistoryHeader>
        * heading
    * orderCountLabel
    * extraActions
    * loading
    * alert, backToShoppingLink
    * emptyMessage, backToShoppingLink
    + list <OrderHistoryList>
        + ...salesOrders <OrderHistorySalesOrder>
            + ...orderInformation <OrderHistoryOrderInfomation>
                * channelName
                * salesId
                * receiptId
                * createdDate
                * count
                * amount
                * channelReferenceId
                * orderReference
            + ...groups <OrderHistoryGroups>
                + ...groups <OrderHistoryGroup>
                    + ...delivery <OrderHistoryGroupDelivery>
                        * heading
                        * count
                        * processing
                        * address
                        * trackingInfo
                    * ...salesLines <React.Fragment>
            * expandProductsButton
            * orderDetailsLink
    * table
    * moreButton
********************************************************************/


/**
 * Generates the order properties for a single sales order.
 */
export const OrderHistoryOrderInfomation: React.FC<IExtendedOrderInformation> = ({
    orderInformationProps,
    channelName,        // "Online Purchase" 
    salesId,     
    receiptId,
    createdDate,        // "1/1/2000"  (inline)
    count,              // "1 item"    (inline)
    amount,             // "$1,199.99" (inline)
    channelReferenceId, // "Confirmation ID: ABCDEFGNAMCO"
    orderReference      // "Order Reference: None provided"
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {channelReferenceId}
        {orderReference}
    </Node>
);

/**
 * Generates the delivery properties for a single sales order.
 */
export const OrderHistoryGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,      
    heading,            // "Delivery"  (inline)
    count,              // "(1 item)"   (inline)
    processing,         // "Processing" (inline)
    address,            // "Ship to \n [Address name] [Full address]"
    trackingInfo        // "Tracking number: Not yet available"
}) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
        {processing}
        {address}
        {trackingInfo}
    </Node>
);

export const OrderHistoryGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderHistoryGroupDelivery {...delivery} />}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map((salesLine: ISalesLine) => (
                    <React.Fragment key={salesLine.data.salesLine.LineId}>{salesLine.salesLine}</React.Fragment>
                ))}
            </Node>
        )}
    </Node>
);

export const OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderHistoryGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderHistoryHeader: React.FC<IHeader> = ({ headerProps, heading, orderCountLabel, extraActions }) => (
    <Node {...headerProps}>
        {heading}
        {orderCountLabel}
        {extraActions}
    </Node>
);

/**
 * Generates a single sales order's section
 */
export const OrderHistorySalesOrder: React.FC<ISalesOrder> = ({
    salesOrderProps,
    orderInfomation,
    orderReference,
    groups,
    orderDetailsLink,
    expandProductsButton
}) => (
    <Node {...salesOrderProps}>
        {orderInfomation && <OrderHistoryOrderInfomation { ...orderInfomation} orderReference={orderReference} />}
        {groups && <OrderHistoryGroups {...groups} />}
        {expandProductsButton}
        {orderDetailsLink}
    </Node>
);

/**
 * Generates a list of sales orders.
 * For table generation, see .tsx and table-view component
 */
export const OrderHistoryList: React.FC<IList> = ({ listProps, salesOrders }) => (
    <Node {...listProps}>
        {salesOrders && salesOrders.map((salesOrder: ISalesOrder, index: number) => <OrderHistorySalesOrder key={index} {...salesOrder} />)}
    </Node>
);

/**
 * Generates the entire history
 */
const OrderHistoryView: React.FC<IOrderHistoryViewProps> = ({
    orderHistoryProps,
    header,
    alert,
    loading,
    emptyMessage,
    backToShoppingLink,
    list,
    table,
    moreButton
}) => (
    <Module {...orderHistoryProps}>
        {header && <OrderHistoryHeader {...header} />}
        {loading}
        {alert && (
            <>
                {alert}
                {backToShoppingLink}
            </>
        )}
        {emptyMessage && (
            <>
                {emptyMessage}
                {backToShoppingLink}
            </>
        )}
        {list && <OrderHistoryList {...list} />}
        {table}
        {moreButton && moreButton}
    </Module>
);

export default OrderHistoryView;
